import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const THEME_STORAGE_KEY = 'makaya_theme';
const PUBLIC_THEME = 'light';
const ADMIN_THEME = 'dark';
const THEMES = ['dark', 'light'];

const ThemeContext = createContext(null);

function getThemeForPath(pathname = '') {
  return pathname.startsWith('/admin') ? ADMIN_THEME : PUBLIC_THEME;
}

function getInitialTheme() {
  if (typeof window === 'undefined') return PUBLIC_THEME;
  return getThemeForPath(window.location.pathname || '/');
}

function normalizeTheme(nextTheme, currentPathname) {
  if (!THEMES.includes(nextTheme)) return getThemeForPath(currentPathname);
  if ((currentPathname || '').startsWith('/admin')) return ADMIN_THEME;
  return PUBLIC_THEME;
}

function persistTheme(theme) {
  if (typeof window === 'undefined') return;
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored !== theme) window.localStorage.setItem(THEME_STORAGE_KEY, theme);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  const setTheme = useCallback((nextTheme) => {
    const currentPathname = typeof window === 'undefined' ? '' : window.location.pathname || '/';
    const validTheme = normalizeTheme(nextTheme, currentPathname);
    setThemeState(validTheme);
    persistTheme(validTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', theme);
    document.body.classList.toggle('theme-light', theme === 'light');
    document.body.classList.toggle('theme-dark', theme === 'dark');
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === 'dark',
      isLight: theme === 'light',
      setTheme,
      toggleTheme,
    }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
